import React, { useState, useRef, useEffect, useCallback } from 'react'
import { AutorizacaoTimeline, Input } from '../../../components/'
import type { FixedRolePreviewConfig } from '../../../components/AutorizacaoTimeline'
import { Select, UserMultiSelect, UnifiedProcessSelector } from '../../../components/'
import { Textarea } from '../../../components/'
import { Button } from '../../../components/'
import { Loading, LoadingInline } from '../../../components/'
import { RichTextDisplay } from '../../../components/RichTextDisplay'
import {
  HiChevronRight,
  HiPaperAirplane,
  HiPaperClip,
  HiArrowLeft,
  HiXMark,
  HiChevronDown,
  HiChevronUp,
  HiDocumentText,
  HiCalendar,
  HiUsers,
  HiCheckCircle,
  HiClipboardDocumentList,
  HiExclamationCircle,
  HiBuildingOffice2,
  HiUser,
  HiCog,
  HiLink,
  HiCog6Tooth,
  HiOutlineLightBulb,
} from 'react-icons/hi2'
import { useNewDemandLogic } from './logic-progressive'
import { cn } from '../../../utils/formatter'
import { useKeyboardShortcut } from '../../../hooks/useKeyboardShortcut'
import { useKeyboardShortcutsContext } from '../../../contexts/KeyboardShortcutsContext'
import { useNavigate } from 'react-router-dom'
import { authService } from '../../../services/auth'
import { api } from '../../../services/axios'
import { TemplateFormRenderer } from '../../templates'
import type { TemplateSchema } from '../../templates'
import type { ProcessoOption, ProcessoAutorizador } from './logic-progressive'
import type { DemandaAutorizacao } from '../../demand-view/hooks/queries/useAutorizacoesQuery'

// Tipos para as etapas do formulário
type FormStep =
  | 'process-selection'
  | 'process-customization'
  | 'demand-details'
  | 'identification'
  | 'confirmation'

type ProcessoTimelineAutorizador = ProcessoAutorizador & {
  nome_setor?: string | null
  nome?: string | null
}

type ProcessoTimelineSource = Pick<
  ProcessoOption,
  'autorizadores' | 'gestor_demandante' | 'executivo_demandante' | 'executivo_demandado' | 'gestor_demandado'
>

type AutorizadorPreview = ProcessoTimelineAutorizador


interface StepContainerProps {
  isActive: boolean
  isCompleted: boolean
  children: React.ReactNode
  className?: string
}

// Componente de progresso de upload
const UploadProgressIndicator: React.FC<{
  progress: number
  status: 'idle' | 'validating' | 'creating' | 'uploading' | 'success' | 'error'
  message: string
  isVisible: boolean
}> = ({ progress, status, message, isVisible }) => {
  if (!isVisible) return null

  const getStatusColor = () => {
    switch (status) {
      case 'validating':
        return 'text-blue-600 dark:text-blue-400'
      case 'creating':
        return 'text-purple-600 dark:text-purple-400'
      case 'uploading':
        return 'text-primary'
      case 'success':
        return 'text-emerald-600 dark:text-emerald-400'
      case 'error':
        return 'text-red-600 dark:text-red-400'
      default:
        return 'text-muted-foreground'
    }
  }

  const getStatusIcon = () => {
    switch (status) {
      case 'validating':
        return '🔍'
      case 'creating':
        return '⚡'
      case 'uploading':
        return '📤'
      case 'success':
        return '✅'
      case 'error':
        return '❌'
      default:
        return '⏳'
    }
  }

  const getProgressBarColor = () => {
    switch (status) {
      case 'validating':
        return 'bg-blue-500 dark:bg-blue-400'
      case 'creating':
        return 'bg-purple-500 dark:bg-purple-400'
      case 'uploading':
        return 'bg-primary'
      case 'success':
        return 'bg-emerald-500 dark:bg-emerald-400'
      case 'error':
        return 'bg-red-500 dark:bg-red-400'
      default:
        return 'bg-muted-foreground'
    }
  }

  return (
    <div className='animate-in fade-in-0 slide-in-from-bottom-4 duration-500'>
      <div className='bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-xl p-6 shadow-lg'>
        {/* Header do progresso */}
        <div className='flex items-center justify-between mb-4'>
          <div className='flex items-center space-x-3'>
            <div className='text-2xl animate-pulse'>{getStatusIcon()}</div>
            <div>
              <h3 className='font-semibold text-gray-900 dark:text-gray-100 text-lg'>
                Enviando demanda
              </h3>
              <p
                className={cn(
                  'text-sm font-medium transition-colors duration-300',
                  getStatusColor()
                )}
              >
                {message}
              </p>
            </div>
          </div>
          <div className='text-right'>
            <div className='text-2xl font-bold text-gray-900 dark:text-gray-100'>{progress}%</div>
            <div className='text-xs text-muted-foreground dark:text-gray-400'>concluído</div>
          </div>
        </div>

        {/* Barra de progresso animada */}
        <div className='w-full bg-muted/30 rounded-full h-3 overflow-hidden'>
          <div
            className={cn(
              'h-full rounded-full transition-all duration-500 ease-out relative overflow-hidden',
              getProgressBarColor()
            )}
            style={{ width: `${progress}%` }}
          >
            {/* Efeito de shimmer */}
            <div className='absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer'></div>
          </div>
        </div>

        {/* Indicador animado para status ativo */}
        {(status === 'validating' || status === 'creating' || status === 'uploading') && (
          <div className='flex justify-center mt-4'>
            <LoadingInline size='sm' />
          </div>
        )}
      </div>
    </div>
  )
}

const StepContainer: React.FC<StepContainerProps> = ({
  isActive,
  isCompleted,
  children,
  className = '',
}) => {
  return (
    <div
      className={cn(
        'transition-all duration-700 ease-out',
        isActive
          ? 'opacity-100 scale-100 translate-y-0 animate-in fade-in-0 slide-in-from-bottom-4 relative z-10'
          : isCompleted
            ? 'opacity-0 scale-95 translate-y-4 pointer-events-none absolute inset-0 z-0'
            : 'opacity-0 scale-95 translate-y-8 pointer-events-none absolute inset-0 z-0',
        className
      )}
    >
      {children}
    </div>
  )
}

// Componente de Header do Template
interface TemplateHeaderProps {
  templateNome: string
  templateDescricao?: string | undefined
  processoNome?: string
  className?: string
}

const TemplateHeader: React.FC<TemplateHeaderProps> = ({
  templateNome,
  templateDescricao,
  //processoNome,
  className = '',
}) => {
  return (
    <div
      className={cn(
        'bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-xl p-6 mb-6',
        'border dark:border-primary/30 rounded-xl p-6 mb-6',
        'shadow-sm hover:shadow-md transition-all duration-300',
        className
      )}
    >
      <div className='flex items-start space-x-4'>
        <div className='flex-shrink-0'>
          <div className='w-12 h-12 rounded-lg bg-primary/10 dark:bg-primary/20 flex items-center justify-center'>
            <HiDocumentText className='w-6 h-6 text-primary dark:text-blue-400' />
          </div>
        </div>
        <div className='flex-1 min-w-0'>
          <h3 className='text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1'>
            {templateNome}
          </h3>
          {/* {processoNome && (
            <p className='text-sm text-gray-600 dark:text-gray-400 mb-2 flex items-center space-x-2'>
              <HiBuildingOffice2 className='w-4 h-4' />
              <span>{processoNome}</span>
            </p>
          )} */}
          {templateDescricao && (
            <p className='text-sm text-gray-700 dark:text-gray-300 leading-relaxed'>
              {templateDescricao}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

// Componente de Header Sticky
interface FormHeaderProps {
  currentStep: FormStep
  customizationMode: 'custom' | 'simple'
  fluxoSimplificadoAtivo: boolean
  canGoBack: boolean
  canGoNext: boolean
  onBack: () => void
  onNext: () => void
  isSubmitting: boolean
  uploadStatus: 'idle' | 'validating' | 'creating' | 'uploading' | 'success' | 'error'
  onSubmit?: () => void
}

const FormHeader: React.FC<FormHeaderProps> = ({
  currentStep,
  customizationMode,
  fluxoSimplificadoAtivo,
  canGoBack,
  canGoNext,
  onBack,
  onNext,
  isSubmitting,
  uploadStatus,
  onSubmit,
}) => {
  // Configuração de steps baseado no modo
  const steps = fluxoSimplificadoAtivo
    ? [
        { id: 'process-selection', label: 'Processo', number: 1 },
        { id: 'demand-details', label: 'Formulário', number: 2 },
      ]
    : customizationMode === 'custom'
      ? [
          { id: 'process-selection', label: 'Processos', number: 1 },
          { id: 'process-customization', label: 'Configuração', number: 2 },
          { id: 'identification', label: 'Identificação', number: 3 },
          { id: 'confirmation', label: 'Confirmação', number: 4 },
        ]
      : [
          { id: 'process-selection', label: 'Processos', number: 1 },
          { id: 'demand-details', label: 'Detalhes', number: 2 },
          { id: 'identification', label: 'Identificação', number: 3 },
          { id: 'confirmation', label: 'Confirmação', number: 4 },
        ]

  const currentStepIndex = steps.findIndex(s => s.id === currentStep)
  const isLastStep = currentStepIndex === steps.length - 1

  // Títulos e subtítulos dinâmicos
  const getStepInfo = () => {
    switch (currentStep) {
      case 'process-selection':
        return {
          title: 'Selecione os Processos',
          subtitle: 'Escolha um ou mais processos para sua demanda',
        }
      case 'process-customization':
        return {
          title: 'Configure os Processos',
          subtitle: 'Preencha os detalhes específicos de cada processo',
        }
      case 'demand-details':
        return {
          title: 'Detalhes da Demanda',
          subtitle: 'Informe os detalhes gerais da sua solicitação',
        }
      case 'identification':
        return {
          title: 'Identificação',
          subtitle: 'Defina demandante e observadores',
        }
      case 'confirmation':
        return {
          title: 'Revisão Final',
          subtitle: 'Confira todos os dados antes de enviar',
        }
      default:
        return { title: '', subtitle: '' }
    }
  }

  const stepInfo = getStepInfo()

  // Step 1: Mostrar apenas o botão de continuar
  if (currentStep === 'process-selection') {
    return (
      <div className='flex-shrink-0 px-6 py-3'>
        <div className='max-w-3xl mx-auto'>
          <div className='flex justify-end'>
            <Button
              onClick={onNext}
              disabled={!canGoNext || isSubmitting || uploadStatus !== 'idle'}
              variant='primary'
              className='px-4 py-1.5 text-sm'
            >
              <span>Continuar</span>
              <HiChevronRight className='w-4 h-4 ml-1.5' />
            </Button>
          </div>
        </div>
      </div>
    )
  }

  // Steps 2+: Header completo
  return (
    <div className='flex-shrink-0 px-6 py-3'>
      <div className='max-w-3xl mx-auto'>
        {/* Card do Header - Minimalista */}
        <div className='bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-xl p-4 shadow-sm'>
          <div className='flex items-center justify-between gap-6'>
            {/* Botão Voltar */}
            <Button
              variant='outline'
              onClick={onBack}
              disabled={!canGoBack || isSubmitting || uploadStatus !== 'idle'}
              className='px-3 py-1.5 text-sm flex-shrink-0'
            >
              <HiArrowLeft className='w-4 h-4 mr-1.5' />
              Voltar
            </Button>

            {/* Centro: Steps acima, Título e Subtítulo alinhados */}
            <div className='flex-1 flex flex-col items-center justify-center'>
              {/* Step Indicators - Pequeno acima */}
              <div className='flex items-center gap-1.5 mb-2'>
                {steps.map((step, index) => (
                  <React.Fragment key={step.id}>
                    <div
                      className={cn(
                        'flex items-center justify-center w-5 h-5 rounded-full text-xs font-semibold transition-all duration-300',
                        currentStepIndex === index
                          ? 'bg-blue-600 dark:bg-blue-500 text-white shadow-sm'
                          : currentStepIndex > index
                            ? 'bg-emerald-500 dark:bg-emerald-600 text-white'
                            : 'bg-gray-200 dark:bg-neutral-700 text-gray-600 dark:text-gray-300'
                      )}
                    >
                      {currentStepIndex > index ? (
                        <HiCheckCircle className='w-3 h-3' />
                      ) : (
                        step.number
                      )}
                    </div>
                    {index < steps.length - 1 && (
                      <div
                        className={cn(
                          'w-6 h-0.5 transition-all duration-300',
                          currentStepIndex > index
                            ? 'bg-emerald-500 dark:bg-emerald-600'
                            : currentStepIndex === index
                              ? 'bg-blue-600 dark:bg-blue-500'
                              : 'bg-gray-200 dark:bg-neutral-700'
                        )}
                      />
                    )}
                  </React.Fragment>
                ))}
              </div>

              {/* Título e Subtítulo - Alinhados */}
              <div className='text-center'>
                <h1 className='text-base font-semibold text-gray-900 dark:text-gray-100 leading-tight'>
                  {stepInfo.title}
                </h1>
                <p className='text-xs text-gray-500 dark:text-gray-400'>{stepInfo.subtitle}</p>
              </div>
            </div>

            {/* Botão Continuar/Enviar */}
            {isLastStep ? (
              <Button
                onClick={onSubmit}
                disabled={
                  isSubmitting ||
                  uploadStatus !== 'idle' ||
                  (fluxoSimplificadoAtivo && currentStep === 'demand-details' && !canGoNext)
                }
                variant='primary'
                className='px-4 py-1.5 text-sm flex-shrink-0'
                translate="no"
              >
                {uploadStatus === 'success' ? (
                  <>
                    <div className='w-4 h-4 mr-1.5 text-emerald-500'>✅</div>
                    <span>Enviado!</span>
                  </>
                ) : isSubmitting || uploadStatus !== 'idle' ? (
                  <>
                    <LoadingInline size='xs' />
                    <span className='ml-1.5 text-xs'>
                      {uploadStatus === 'validating'
                        ? 'Validando...'
                        : uploadStatus === 'creating'
                          ? 'Criando...'
                          : uploadStatus === 'uploading'
                            ? 'Enviando...'
                            : 'Processando...'}
                    </span>
                  </>
                ) : (
                  <>
                    <HiPaperAirplane className='w-4 h-4 mr-1.5' />
                    <span>Enviar</span>
                  </>
                )}
              </Button>
            ) : (
              <Button
                onClick={onNext}
                disabled={!canGoNext || isSubmitting || uploadStatus !== 'idle'}
                variant='primary'
                className='px-4 py-1.5 text-sm flex-shrink-0'
              >
                <span>Continuar</span>
                <HiChevronRight className='w-4 h-4 ml-1.5' />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export const ProgressiveDemandForm: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<FormStep>('process-selection')
  const [completedSteps, setCompletedSteps] = useState<Set<FormStep>>(new Set())
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [expandedProcesses, setExpandedProcesses] = useState<Set<string | number>>(new Set())
  const [expandedProcessesStep2, setExpandedProcessesStep2] = useState<Set<string | number>>(
    new Set()
  )

  const navigate = useNavigate()
  const { enabled: shortcutsEnabled } = useKeyboardShortcutsContext()

  // Auto-scroll para o topo quando mudar de step
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    }
  }, [currentStep])

  // Handler para download de anexos autenticado
  const handleDownloadAttachment = async (anexoObj: { anexo: string; nome_anexo?: string }) => {
    let link: HTMLAnchorElement | null = null;
    let downloadUrl: string | null = null;

    try {
      // Garantir que Sanctum esteja inicializado
      await authService.ensureSanctumInitialized()

      const anexoPath = anexoObj.anexo
      let endpoint: string

      // Determinar o endpoint correto baseado no caminho
      if (anexoPath.includes('processos/anexos/')) {
        const filename = anexoPath.split('/').pop() || anexoPath
        endpoint = `/storage/processos/anexos/${encodeURIComponent(filename)}`
      } else if (anexoPath.includes('anexos/')) {
        const filename = anexoPath.split('/').pop() || anexoPath
        endpoint = `/storage/anexos/${encodeURIComponent(filename)}`
      } else {
        const filename = anexoPath.split('/').pop() || anexoPath
        endpoint = `/storage/processos/anexos/${encodeURIComponent(filename)}`
      }

      // Fazer requisição autenticada para baixar o blob
      const response = await api.get(endpoint, {
        responseType: 'blob',
        headers: {
          Accept: '*/*',
        },
      })

      // Criar URL do blob e fazer download
      const blob = response.data
      downloadUrl = URL.createObjectURL(blob)

      link = document.createElement('a')
      link.href = downloadUrl
      link.download = anexoObj.nome_anexo || 'template'
      document.body.appendChild(link)
      link.click()

      // Aguardar um pouco antes de remover para garantir que o download iniciou
      await new Promise(resolve => setTimeout(resolve, 100))

    } catch (error) {
      console.error('Erro ao fazer download do anexo:', error)
      // Fallback: tentar método antigo
      const baseUrl = import.meta.env.VITE_API_URL
      const anexoPath = anexoObj.anexo
      const anexoUrl = anexoPath?.startsWith('http') ? anexoPath : `${baseUrl}/storage/${anexoPath}`
      window.open(anexoUrl, '_blank')
    } finally {
      // Cleanup seguro: remove() não lança erro se o elemento não estiver no DOM
      link?.remove();
      
      // Limpar o objeto URL
      if (downloadUrl) {
        URL.revokeObjectURL(downloadUrl);
      }
    }
  }

  const {
    // Estados do formulário
    titulo,
    prioridade,
    descricao,
    dataEntrega,
    demandante,
    euCriei,
    setEuCriei,
    observadores,
    anexos,

    // Estados específicos do formulário progressivo
    selectedProcesses,
    setSelectedProcesses,
    selectedSectors,
    setSelectedSectors,

    // Estados de customização
    customizationMode,
    processCustomData,
    setProcessCustomData,

    // Estado para respostas de templates dinâmicos
    templateRespostas,
    setTemplateRespostas,

    // Estados de SLA
    //slaData,
    loadingSLA,

    // Opções para os selects
    processoOptions,
    userOptions,
    setorOptions,
    prioridadeOptions,

    // Estados de loading e erro
    loading,
    isSubmitting,
    submitError,
    fieldErrors,
    errorUsuarios,
    errorSetores,

    // Estados de upload progressivo
    uploadProgress,
    uploadStatus,
    uploadMessage,

    // Handlers específicos com validação
    handleTituloChange,
    handlePrioridadeChange,
    handleDescricaoChange,
    handleDataEntregaChange,
    handleDemandanteChange,
    handleObservadoresChange,

    // Handlers
    handleFileChange,
    handleEnviar,

    // Funções de fluxo simplificado
    getFluxoSimplificado,
    aplicarFluxoSimplificado,
    
    // ID da demanda relacionada (se vier da URL)
    relacionadaFk,
  } = useNewDemandLogic()

  const fluxoSimplificadoAtivo = getFluxoSimplificado()
  const isFluxoSimplificado = Boolean(fluxoSimplificadoAtivo)

  // Expande automaticamente os processos quando são selecionados (Step 2)
  useEffect(() => {
    if (selectedProcesses.length > 0) {
      setExpandedProcessesStep2(new Set(selectedProcesses))
    }
  }, [selectedProcesses])

  // Estado para rastrear se o fluxo simplificado já foi aplicado para o processo atual
  const [fluxoSimplificadoAplicado, setFluxoSimplificadoAplicado] = useState<string | number | null>(null)

  // Detectar e aplicar fluxo simplificado automaticamente quando processo é selecionado
  useEffect(() => {
    if (fluxoSimplificadoAtivo && fluxoSimplificadoAplicado !== fluxoSimplificadoAtivo.processoId) {
      aplicarFluxoSimplificado(fluxoSimplificadoAtivo)
      setFluxoSimplificadoAplicado(fluxoSimplificadoAtivo.processoId)
      setTimeout(() => {
        setCompletedSteps(new Set(['process-selection']))
        setCurrentStep('demand-details')
      }, 100)
    }

    if (!fluxoSimplificadoAtivo && fluxoSimplificadoAplicado !== null) {
      setFluxoSimplificadoAplicado(null)
    }
  }, [fluxoSimplificadoAtivo, fluxoSimplificadoAplicado, aplicarFluxoSimplificado])

  // Keyboard shortcuts for demand form
  useKeyboardShortcut(
    'ctrl+enter',
    () => {
      // Prevent browser save dialog
      if (shortcutsEnabled) {
        // Espaço reservado para atalho de salvar futuramente
      }
    },
    {
      enabled: shortcutsEnabled,
      description: 'Salvar Demanda',
      category: 'demands',
    }
  )

  useKeyboardShortcut(
    'ctrl+enter',
    () => {
      if (shortcutsEnabled && uploadStatus !== 'creating' && uploadStatus !== 'uploading') {
        handleEnviar()
      }
    },
    {
      enabled: shortcutsEnabled,
      description: 'Enviar/Submeter Demanda',
      category: 'demands',
    }
  )

  useKeyboardShortcut(
    'escape',
    () => {
      if (shortcutsEnabled) {
        navigate('/demands')
      }
    },
    {
      enabled: shortcutsEnabled,
      description: 'Cancelar/Voltar',
      category: 'demands',
    }
  )

  // Handler personalizado para adicionar arquivos (não substituir)
  const handleAddFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files)
      const allowedTypes = [
        'application/pdf',
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/plain',
        'application/zip',
        'application/x-rar-compressed',
      ]

      const validFiles: File[] = []

      newFiles.forEach(file => {
        // Verificar se o arquivo já existe
        const fileExists = anexos.some(
          existingFile => existingFile.name === file.name && existingFile.size === file.size
        )

        if (fileExists) {
          return // Pular arquivo duplicado
        }

        const fileExtension = file.name.toLowerCase().split('.').pop()
        const allowedExtensions = [
          'pdf',
          'jpg',
          'jpeg',
          'png',
          'gif',
          'doc',
          'docx',
          'xls',
          'xlsx',
          'txt',
          'zip',
          'rar',
        ]

        if (allowedTypes.includes(file.type) || allowedExtensions.includes(fileExtension || '')) {
          validFiles.push(file)
        }
      })

      // Adicionar aos arquivos existentes
      const updatedFiles = [...anexos, ...validFiles]

      handleFileChange(updatedFiles)

      // Limpar o input
      e.target.value = ''
    }
  }

  // Handler para remover arquivo individual
  const handleRemoveFile = (indexToRemove: number) => {
    const updatedFiles = anexos.filter((_, index) => index !== indexToRemove)

    handleFileChange(updatedFiles)
  }

  const buildProcessTimelinePreview = (proc?: ProcessoTimelineSource) => {
    const autorizadores: AutorizadorPreview[] = Array.isArray(proc?.autorizadores)
      ? (proc?.autorizadores as AutorizadorPreview[])
      : []

    const fixedRolePreview: FixedRolePreviewConfig = {
      gestorDemandante: Boolean(proc?.gestor_demandante),
      executivoDemandante: Boolean(proc?.executivo_demandante),
      executivoDemandado: Boolean(proc?.executivo_demandado),
      gestorDemandado: Boolean(proc?.gestor_demandado),
    }

    const hasFixedRoles =
      fixedRolePreview.gestorDemandante ||
      fixedRolePreview.executivoDemandante ||
      fixedRolePreview.executivoDemandado ||
      fixedRolePreview.gestorDemandado

    const hasCustomAutorizadores = autorizadores.length > 0
    const isSequencial =
      hasCustomAutorizadores &&
      autorizadores.some(aut => aut?.pivot?.ordem !== null && aut?.pivot?.ordem !== undefined)

    const timelineIsSequencial = Boolean(isSequencial || hasFixedRoles)

    const timestamp = new Date().toISOString()

    const autorizacoesVisuais: DemandaAutorizacao[] = autorizadores.map((aut, index) => {
      const pivotBase = aut.pivot
      const pivot = pivotBase
        ? {
            id: Number(`${aut.id}${index}`),
            processo_fk: pivotBase.processo_fk,
            autorizador_fk: pivotBase.autorizador_fk,
            ordem: pivotBase.ordem ?? null,
            active: Boolean(pivotBase.active),
            created_at: pivotBase.created_at ?? timestamp,
            updated_at: pivotBase.updated_at ?? timestamp,
          }
        : undefined

      return {
        id: Number(`${aut.id}${index}`),
        demanda_fk: 0,
        autorizador_fk: aut.id,
        ordem: pivotBase?.ordem ?? null,
        status: 'AGUARDANDO',
        created_at: timestamp,
        updated_at: timestamp,
        autorizador: {
          id: aut.id,
          nome_setor: aut.nome_setor ?? aut.nome ?? 'Autorizador',
          equipe: false,
          ...(pivot ? { pivot } : {}),
        },
      }
    })

    return {
      fixedRolePreview,
      shouldRenderTimeline: hasCustomAutorizadores || hasFixedRoles,
      timelineIsSequencial,
      autorizacoesVisuais,
    }
  }

  // Função auxiliar para obter o stepOrder correto
  const getStepOrder = useCallback((): FormStep[] => {
    if (isFluxoSimplificado) {
      return ['process-selection', 'demand-details']
    }
    if (customizationMode === 'custom') {
      return [
        'process-selection',
        'process-customization',
        'identification',
        'confirmation',
      ]
    }
    return ['process-selection', 'demand-details', 'identification', 'confirmation']
  }, [customizationMode, isFluxoSimplificado])

  useEffect(() => {
    const stepOrder = getStepOrder()
    if (!stepOrder.includes(currentStep)) {
      const fallbackStep = stepOrder.includes('process-customization')
        ? 'process-customization'
        : stepOrder[0]
      setCurrentStep(fallbackStep)
    }

    setCompletedSteps(prev => {
      const filtered = new Set(Array.from(prev).filter(step => stepOrder.includes(step)))
      return filtered.size === prev.size ? prev : filtered
    })
  }, [customizationMode, currentStep, getStepOrder])

  // Função para avançar para próxima etapa
  const handleNextStep = () => {
    // Definir ordem de etapas baseado no modo
    const stepOrder = getStepOrder()

    const currentIndex = stepOrder.indexOf(currentStep)

    // Marcar etapa atual como completa
    setCompletedSteps(prev => new Set([...prev, currentStep]))

    // Avançar para próxima etapa se não for a última
    if (currentIndex < stepOrder.length - 1) {
      setTimeout(() => {
        setCurrentStep(stepOrder[currentIndex + 1])
      }, 300) // Delay para suavizar a transição
    }
  }

  // Função para voltar etapa
  const handlePreviousStep = () => {
    // Definir ordem de etapas baseado no modo
    const stepOrder = getStepOrder()

    const currentIndex = stepOrder.indexOf(currentStep)

    if (currentIndex > 0) {
      // Remove o step atual e o step anterior dos completed para permitir navegação
      setCompletedSteps(prev => {
        const newSet = new Set(prev)
        newSet.delete(currentStep)
        newSet.delete(stepOrder[currentIndex - 1]) // Remove o step anterior também
        return newSet
      })

      // Muda imediatamente para o step anterior
      setCurrentStep(stepOrder[currentIndex - 1])
    }
  }

  // Obter processos selecionados (movido para antes de canProceedToNext)
  // Normaliza IDs para number para evitar problemas de comparação string vs number
  const normalizedSelectedProcesses = selectedProcesses.map(id => Number(id))
  const selectedProcessObjects = processoOptions.filter(p => 
    normalizedSelectedProcesses.includes(Number(p.value))
  ) as ProcessoOption[]

  const tituloBlockado =
    selectedProcessObjects.length === 1 && !!selectedProcessObjects[0].template_schema?.blockTitulo

  // Verificar se algum processo selecionado tem template_schema
  const processosComTemplate = selectedProcessObjects.filter(proc => proc.template_schema)
  const temProcessoComTemplate = processosComTemplate.length > 0

  // Verificar se pode avançar para próxima etapa
  const canProceedToNext = () => {
    switch (currentStep) {
      case 'process-selection':
        return selectedProcesses.length > 0
      case 'process-customization':
        // Verificar se todos os processos têm descrição preenchida
        return selectedProcesses.every(processId => {
          const customData = processCustomData.get(processId)
          return customData && customData.descricao.trim() !== ''
        })
      case 'demand-details': {
        // Validações básicas sempre necessárias
        const basicFieldsValid = titulo.trim() !== '' && prioridade !== '' && dataEntrega !== ''
        
        if (!basicFieldsValid) return false
        
        // Verificar descrição OU template
        // Se tem processo com template e apenas 1 selecionado, valida pelo templateRespostas
        if (temProcessoComTemplate && selectedProcesses.length === 1) {
          // Tentar buscar com ambos os tipos de chave (string e number)
          const processId = selectedProcesses[0]
          const templateData = templateRespostas.get(processId) || templateRespostas.get(Number(processId)) || templateRespostas.get(String(processId))
          // Verifica se há pelo menos um campo preenchido no template
          const processoComTemplate = processosComTemplate[0]
          const schema = processoComTemplate?.template_schema?.schema
          const requiredFields = Array.isArray(schema?.required) ? (schema.required as string[]) : []
          
          // Se há campos obrigatórios, verifica se estão preenchidos
          if (requiredFields.length > 0) {
            if (!templateData) return false
            const properties = schema?.properties || {}
            return requiredFields.every((field: string) => {
              const value = templateData[field]
              const fieldDef = (properties as Record<string, unknown>)[field]
              const isArray =
                fieldDef &&
                typeof fieldDef === 'object' &&
                !Array.isArray(fieldDef) &&
                'type' in (fieldDef as object) &&
                (fieldDef as Record<string, unknown>)['type'] === 'array'

              if (isArray) {
                if (!Array.isArray(value) || value.length === 0) return false
                const itemDef =
                  'items' in (fieldDef as object)
                    ? (fieldDef as Record<string, unknown>)['items']
                    : undefined
                const itemRequired: string[] =
                  itemDef &&
                  typeof itemDef === 'object' &&
                  !Array.isArray(itemDef) &&
                  'required' in (itemDef as object) &&
                  Array.isArray((itemDef as Record<string, unknown>)['required'])
                    ? ((itemDef as Record<string, unknown>)['required'] as string[])
                    : []
                return value.every((item: Record<string, unknown>) =>
                  itemRequired.every(
                    req =>
                      item[req] !== undefined &&
                      item[req] !== null &&
                      !(typeof item[req] === 'string' && (item[req] as string).trim() === '')
                  )
                )
              }

              return value !== undefined && value !== null && value !== ''
            })
          }
          
          // Se não há campos obrigatórios definidos, aceita qualquer preenchimento
          return templateData ? Object.keys(templateData).length > 0 : false
        }
        
        // Sem template: exige descrição preenchida
        return descricao.trim() !== ''
      }
      case 'identification':
        return euCriei || demandante !== ''
      default:
        return true
    }
  }

  // Handler para atualizar respostas do template
  const handleTemplateChange = (processoId: string | number, formData: Record<string, unknown>) => {
    setTemplateRespostas(prev => {
      const newMap = new Map(prev)
      newMap.set(processoId, formData)
      return newMap
    })
  }

  if (loading) {
    return (
      <div className='min-h-screen flex items-center justify-center min-w-full m'>
        <div className='text-center space-y-4 flex flex-col items-center'>
          <Loading size='lg' message='Preparando tudo para você...' />
          <div className='space-y-2'>
            <h3 className='text-lg font-semibold text-gray-900 dark:text-gray-100'>
              Carregando formulário
            </h3>
            {(errorUsuarios || errorSetores) && (
              <div className='mt-4 p-4 bg-destructive/10 border border-destructive/20 rounded-lg'>
                <p className='text-sm text-destructive font-medium'>
                  ⚠️ Problema de conexão detectado
                </p>
                <p className='text-xs text-destructive/80 mt-1'>
                  Verifique se o backend está rodando em http://localhost:8000
                </p>
                {errorUsuarios && (
                  <p className='text-xs text-destructive/70'>Usuários: {errorUsuarios}</p>
                )}
                {errorSetores && (
                  <p className='text-xs text-destructive/70'>Setores: {errorSetores}</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Verificar se pode voltar
  const canGoBack = () => {
    const stepOrder = getStepOrder()
    return stepOrder.indexOf(currentStep) > 0
  }

  // Toggle de processos expandidos (Step 4)
  const toggleProcessExpanded = (processValue: string | number) => {
    setExpandedProcesses(prev => {
      const newSet = new Set(prev)
      if (newSet.has(processValue)) {
        newSet.delete(processValue)
      } else {
        newSet.add(processValue)
      }
      return newSet
    })
  }

  // Toggle de processos expandidos (Step 2)
  const toggleProcessExpandedStep2 = (processValue: string | number) => {
    setExpandedProcessesStep2(prev => {
      const newSet = new Set(prev)
      if (newSet.has(processValue)) {
        newSet.delete(processValue)
      } else {
        newSet.add(processValue)
      }
      return newSet
    })
  }

  // Função para formatar data SLA
  // const formatSLADate = (dateStr: string): string => {
  //   try {
  //     // A data vem no formato ISO: "2025-10-27T05:00:00.000000Z"
  //     const date = new Date(dateStr)
  //     const diasSemana = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado']
  //     const meses = [
  //       'Jan',
  //       'Fev',
  //       'Mar',
  //       'Abr',
  //       'Mai',
  //       'Jun',
  //       'Jul',
  //       'Ago',
  //       'Set',
  //       'Out',
  //       'Nov',
  //       'Dez',
  //     ]

  //     // Usar UTC para evitar problemas de timezone
  //     const diaSemana = diasSemana[date.getUTCDay()]
  //     const dia = date.getUTCDate()
  //     const mes = meses[date.getUTCMonth()]

  //     return `${dia} ${mes} (${diaSemana})`
  //   } catch (error) {
  //     console.error('Erro ao formatar data SLA:', error, dateStr)
  //     return dateStr
  //   }
  // }

  return (
    <>
      <div className='flex flex-col h-[calc(100vh-85px)] overflow-hidden'>
        {/* Header */}
        <FormHeader
          currentStep={currentStep}
          customizationMode={customizationMode}
          fluxoSimplificadoAtivo={isFluxoSimplificado}
          canGoBack={canGoBack()}
          canGoNext={canProceedToNext()}
          onBack={handlePreviousStep}
          onNext={handleNextStep}
          isSubmitting={isSubmitting}
          uploadStatus={uploadStatus}
          onSubmit={handleEnviar}
        />

        {/* Container de conteúdo scrollável */}
        <div ref={scrollContainerRef} className='flex-1 overflow-y-auto overflow-x-hidden'>
          <div className='w-full max-w-7xl mx-auto px-6 py-1'>
            {/* Badge de Demanda Relacionada - Minimalista */}
            {relacionadaFk && (
              <div className='flex justify-center mb-6 animate-in fade-in-0 slide-in-from-top-4 duration-500'>
                <div className='flex w-sm max-h-[30px] justify-between items-center space-x-2.5 bg-[aliceblue] border border-[#cedbe7] rounded-full p-1 text-sm'>
                  <div className='flex items-center max-h-[22px] space-x-2 bg-white dark:bg-neutral-800 border border-cyan-500/30 dark:border-cyan-400/30 rounded-full px-3 py-1.5'>
                    <HiLink className='w-4 h-4 text-cyan-600 dark:text-cyan-400' />
                    <span className='font-medium text-cyan-900 dark:text-cyan-100'>#{relacionadaFk}</span>
                  </div>
                  <p className='text-cyan-800 dark:text-cyan-200 pr-3'>
                    Relacionada à demanda #{relacionadaFk}
                  </p>
                  <button
                    onClick={() => window.open(`/demands/${relacionadaFk}/view?type=all`, '_blank')}
                    className='mr-2 text-cyan-700 dark:text-cyan-300 hover:text-cyan-900 dark:hover:text-cyan-100 transition-colors'
                    title='Ver demanda relacionada'
                  >
                    <HiOutlineLightBulb className='w-4 h-4' />
                  </button>
                </div>
              </div>
            )}

            {/* Container principal do formulário */}
            <div className='relative'>
              {/* ETAPA 1: Seleção de Processo */}
              <StepContainer
                isActive={currentStep === 'process-selection'}
                isCompleted={completedSteps.has('process-selection')}
              >
                <div className='flex flex-col items-center justify-center max-w-8xl mx-auto'>
                  <div className='w-full max-w-8xl mx-auto flex flex-col items-center justify-center'>
                    {/* Header do Step 1 */}
                    <div className='mb-6 text-center px-4 animate-in fade-in-0 slide-in-from-bottom-4 duration-700'>
                      {/* Ícone acima */}
                      <div className='flex justify-center mb-3'>
                        <HiCog6Tooth
                          className='w-16 h-16 text-gray-700 dark:text-blue-400'
                          style={{ animation: 'spin 8s linear infinite' }}
                        />
                      </div>

                      {/* Título */}
                      <h2 className='text-xl md:text-2xl  text-gray-700 dark:text-blue-400 font-bold bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400 bg-clip-text  mb-2'>
                        Qual engrenagem quer girar hoje?
                      </h2>

                      {/* Subtítulo */}
                      <p className='text-sm text-gray-600 dark:text-gray-400 max-w-xl mx-auto'>
                        Escolha os{' '}
                        <span className='font-semibold text-gray-800 dark:text-gray-200'>
                          processos
                        </span>{' '}
                        que deseja acionar
                      </p>
                    </div>

                    {/* Seletor de processos - totalmente centralizado com espaço para sombras */}
                    <div className='mb-8 px-2 w-full'>
                      <div className='max-w-5xl mx-auto relative'>
                        <UnifiedProcessSelector
                          processes={processoOptions}
                          sectors={setorOptions}
                          selectedProcesses={selectedProcesses}
                          onProcessesChange={setSelectedProcesses}
                          selectedSectors={selectedSectors}
                          onSectorsChange={setSelectedSectors}
                          placeholder='Digite o nome do processo ou setor...'
                          expandable={true}
                          maxHeight={600}
                          singleSelection={true}
                          onProcessSelect={() => {
                            // Auto-avançar para próxima etapa em modo singleSelection
                            handleNextStep()
                          }}
                        />
                      </div>
                    </div>

                    {/* Processos selecionados - sem botão */}
                    {selectedProcesses.length > 0 && (
                      <div className='animate-in fade-in-0 zoom-in-95 duration-500 max-w-4xl mx-auto mb-4 px-4'>
                        <div className='bg-white dark:bg-neutral-800 rounded-2xl border border-gray-200 dark:border-neutral-700 p-4 shadow-lg'>
                          <div className='text-center mb-4'>
                            <p className='text-lg text-primary dark:text-blue-300 font-semibold flex items-center justify-center gap-2'>
                              <HiCheckCircle className='w-5 h-5 text-emerald-600 dark:text-emerald-400' />
                              <span>
                                {selectedProcesses.length} processo
                                {selectedProcesses.length > 1 ? 's' : ''} selecionado
                                {selectedProcesses.length > 1 ? 's' : ''}
                              </span>
                            </p>
                          </div>

                          {/* Grid de badges compacto */}
                          <div className='grid grid-cols-1 md:grid-cols-2 gap-2'>
                            {selectedProcessObjects.map((proc, index) => {
                              const sector = setorOptions.find(
                                s => s.value === proc.setor_processo_fk
                              )
                              const getSectorIcon = (sectorName: string) => {
                                const name = sectorName.toLowerCase()
                                if (name.includes('rh') || name.includes('recursos')) return '🏢'
                                if (name.includes('financeiro') || name.includes('contab'))
                                  return '💰'
                                if (name.includes('ti') || name.includes('tecnologia')) return '🔧'
                                if (name.includes('juridico') || name.includes('legal')) return '⚖️'
                                if (name.includes('marketing') || name.includes('comunicacao'))
                                  return '📢'
                                if (name.includes('gestao') || name.includes('administr'))
                                  return '🎯'
                                if (name.includes('bi') || name.includes('inteligencia'))
                                  return '📊'
                                return '📁'
                              }

                              return (
                                <div
                                  key={index}
                                  className='flex items-center space-x-2 px-3 py-2 bg-gradient-to-r from-emerald-50 to-blue-50 dark:from-emerald-900/20 dark:to-blue-900/20 border border-emerald-200 dark:border-emerald-700/40 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 group min-w-0'
                                >
                                  <span className='text-sm'>
                                    {getSectorIcon(sector?.label || '')}
                                  </span>
                                  <div className='flex flex-col min-w-0 flex-1'>
                                    <span className='text-xs font-bold text-emerald-700 dark:text-emerald-200 uppercase tracking-wide truncate'>
                                      {sector?.label || 'Setor não encontrado'}
                                    </span>
                                    <span className='text-xs font-medium text-gray-800 dark:text-gray-100 leading-tight truncate'>
                                      {proc.label}
                                    </span>
                                  </div>
                                  <button
                                    onClick={() =>
                                      setSelectedProcesses(prev =>
                                        prev.filter(id => id !== proc.value)
                                      )
                                    }
                                    className='p-1 hover:bg-red-100 dark:hover:bg-red-900/50 rounded-full opacity-60 hover:opacity-100 transition-all duration-200 flex-shrink-0'
                                    title='Remover processo'
                                  >
                                    <HiXMark className='w-3 h-3 text-red-500 dark:text-red-400' />
                                  </button>
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </StepContainer>

              {/* NOVA ETAPA: Customização por Processo (apenas se customizationMode === 'custom') */}
              {customizationMode === 'custom' && (
                <StepContainer
                  isActive={currentStep === 'process-customization'}
                  isCompleted={completedSteps.has('process-customization')}
                >
                  <div className='h-full flex flex-col'>
                    <div className='w-full max-w-7xl mx-auto'>
                      {/* Card de Dados Compartilhados */}
                      <div className='bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-xl p-5 shadow-sm mb-4'>
                        <div className='flex items-center space-x-2 mb-4'>
                          <HiCog className='w-5 h-5 text-blue-600 dark:text-blue-400' />
                          <h3 className='text-lg font-bold text-gray-900 dark:text-gray-100'>
                            Dados Compartilhados
                          </h3>
                        </div>
                        <p className='text-xs text-gray-600 dark:text-gray-400 mb-4'>
                          Estes dados serão aplicados a todos os processos selecionados
                        </p>
                        <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                          <div>
                            <label className='text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1 block'>
                              Título Base
                            </label>
                            <Input
                              type='text'
                              value={titulo}
                              onChange={e => handleTituloChange(e.target.value)}
                              placeholder='Digite um título...'
                              className='h-9'
                              disabled={tituloBlockado}
                            />
                          </div>
                          <div>
                            <label className='text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1 block'>
                              Prioridade
                            </label>
                            <Select
                              options={prioridadeOptions}
                              value={prioridade}
                              onChange={handlePrioridadeChange}
                              placeholder='Selecione'
                              className='h-9'
                            />
                          </div>
                          <div>
                            <label className='text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1 block'>
                              Data de Entrega
                            </label>
                            <Input
                              type='date'
                              value={dataEntrega}
                              onChange={e => handleDataEntregaChange(e.target.value)}
                              min={new Date().toISOString().split('T')[0]}
                              className='h-9'
                            />

                            {/* Indicador de preenchimento automático */}
                            {dataEntrega && selectedProcesses.length > 0 && (
                              <p className='text-xs text-gray-600 dark:text-gray-400 mt-1'>
                                💡 Data baseada no SLA (você pode alterar)
                              </p>
                            )}
                          </div>
                        </div>

                        {loadingSLA &&
                          selectedProcesses.length > 0 &&
                          selectedProcessObjects.length === 0 && (
                            <div className='mt-3 flex items-center space-x-2 text-xs text-gray-500'>
                              <div className='w-3 h-3 border-2 border-gray-300 border-t-primary rounded-full animate-spin'></div>
                              <span>Calculando SLA...</span>
                            </div>
                          )}
                      </div>

                      {/* Card de Processos - Recolhíveis */}
                      <div className='bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-xl p-5 shadow-sm'>
                        <div className='flex items-center justify-between mb-4'>
                          <div className='flex items-center space-x-2'>
                            <HiClipboardDocumentList className='w-5 h-5 text-emerald-600 dark:text-emerald-400' />
                            <h3 className='text-lg font-bold text-gray-900 dark:text-gray-100'>
                              Configure cada Processo
                            </h3>
                          </div>
                          <span className='px-3 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded-full text-xs font-semibold'>
                            {selectedProcessObjects.length}
                          </span>
                        </div>

                        <div className='space-y-2'>
                          {selectedProcessObjects.map((proc, index) => {
                            const sector = setorOptions.find(
                              s => s.value === proc.setor_processo_fk
                            )
                            const customData = processCustomData.get(proc.value) || {
                              descricao: '',
                              anexos: [],
                            }
                            const isExpanded = expandedProcessesStep2.has(proc.value)

                            // Preparar dados de autorizações se o processo tiver autorizadores
                            const {
                              shouldRenderTimeline,
                              timelineIsSequencial,
                              autorizacoesVisuais,
                              fixedRolePreview,
                            } = buildProcessTimelinePreview(proc)

                            return (
                              <div
                                key={proc.value}
                                className='bg-gray-50 dark:bg-neutral-900/50 border border-gray-200 dark:border-neutral-700 rounded-lg overflow-hidden'
                              >
                                {/* Header do Processo - Clicável */}
                                <div
                                  className='p-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-neutral-800 transition-colors select-none'
                                  onClick={() => toggleProcessExpandedStep2(proc.value)}
                                >
                                  <div className='flex items-center justify-between'>
                                    <div className='flex items-center space-x-3 flex-1 min-w-0'>
                                      <div className='w-7 h-7 bg-emerald-600 dark:bg-emerald-500 rounded-lg flex items-center justify-center text-white font-bold text-xs flex-shrink-0'>
                                        {index + 1}
                                      </div>
                                      <div className='flex-1 min-w-0'>
                                        <h4 className='font-semibold text-gray-900 dark:text-gray-100 text-sm leading-tight truncate'>
                                          {proc.label}
                                        </h4>
                                        <div className='flex items-center space-x-1.5 mt-0.5'>
                                          <HiBuildingOffice2 className='w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 flex-shrink-0' />
                                          <p className='text-xs text-gray-600 dark:text-gray-400 font-medium truncate'>
                                            {sector?.label}
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                    <div className='flex items-center space-x-2 flex-shrink-0 ml-2'>
                                      {/* Timeline de Autorizações */}
                                      {shouldRenderTimeline && (
                                        <div className='flex items-center gap-2'>
                                          <AutorizacaoTimeline
                                            autorizacoes={autorizacoesVisuais}
                                            isSequencial={timelineIsSequencial}
                                            className='flex-1'
                                            previewMode={true}
                                            fixedRolePreview={fixedRolePreview}
                                          />
                                        </div>
                                      )}
                                      {isExpanded ? (
                                        <HiChevronUp className='w-4 h-4 text-gray-600 dark:text-gray-400' />
                                      ) : (
                                        <HiChevronDown className='w-4 h-4 text-gray-600 dark:text-gray-400' />
                                      )}
                                    </div>
                                  </div>
                                </div>

                                {/* Conteúdo do Processo - Expandível */}
                                {isExpanded && (
                                  <div className='px-3 pb-3 pt-2 border-t border-gray-200 dark:border-neutral-700 space-y-4 bg-white dark:bg-neutral-800/50'>
                                    {/* Links */}
                                    {Array.isArray(proc.links) && proc.links.length > 0 && (
                                        <div>
                                          <div className='flex items-center space-x-1.5 mb-2'>
                                            <HiLink className='w-3.5 h-3.5 text-blue-600 dark:text-blue-400' />
                                            <h5 className='text-xs font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wide'>
                                              Links Úteis
                                            </h5>
                                          </div>
                                          <div className='space-y-2'>
                                          {proc.links.map((linkItem, i) => {
                                            const link =
                                              typeof linkItem === 'string'
                                                ? linkItem
                                                : linkItem.link
                                            const titulo =
                                              typeof linkItem === 'string'
                                                ? null
                                                : linkItem.titulo ?? null

                                              return (
                                                <a
                                                  key={i}
                                                  href={link}
                                                  target='_blank'
                                                  rel='noopener noreferrer'
                                                  className='flex items-center space-x-2 p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors group'
                                                >
                                                  <HiLink className='w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0' />
                                                  <div className='flex-1 min-w-0'>
                                                    {titulo ? (
                                                      <>
                                                        <div className='text-sm font-medium text-blue-900 dark:text-blue-100 group-hover:underline'>
                                                          {titulo}
                                                        </div>
                                                        <div className='text-xs text-blue-600 dark:text-blue-400 truncate'>
                                                          {link}
                                                        </div>
                                                      </>
                                                    ) : (
                                                      <div className='text-sm text-blue-600 dark:text-blue-400 group-hover:underline truncate'>
                                                        {link}
                                                      </div>
                                                    )}
                                                  </div>
                                                </a>
                                              )
                                            })}
                                          </div>
                                        </div>
                                      )}

                                    {/* Anexos do Processo (templates para download) */}
                                    {Array.isArray(proc.anexos) && proc.anexos.length > 0 && (
                                        <div>
                                          <div className='flex items-center space-x-1.5 mb-2'>
                                            <HiPaperClip className='w-3.5 h-3.5 text-purple-600 dark:text-purple-400' />
                                            <h5 className='text-xs font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wide'>
                                              Anexos Disponíveis
                                            </h5>
                                          </div>
                                          <div className='space-y-2'>
                                          {proc.anexos.map((anexo, i) => {
                                            const filePath =
                                              typeof anexo === 'string' ? anexo : anexo.anexo
                                            const fileName =
                                              typeof anexo === 'string'
                                                ? anexo.split('/').pop() || 'template'
                                                : anexo.nome_anexo || 'template'

                                              return (
                                                <button
                                                  key={i}
                                                  type='button'
                                                  onClick={() =>
                                                    handleDownloadAttachment({
                                                    anexo: filePath,
                                                      nome_anexo: fileName,
                                                    })
                                                  }
                                                  className='w-full flex items-center space-x-2 p-2 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors'
                                                >
                                                  <HiPaperClip className='w-4 h-4 text-purple-600 dark:text-purple-400 flex-shrink-0' />
                                                  <span className='text-sm text-purple-900 dark:text-purple-200 flex-1 truncate text-left'>
                                                    {fileName}
                                                  </span>
                                                  <span className='text-xs text-purple-600 dark:text-purple-400 flex-shrink-0'>
                                                    Download
                                                  </span>
                                                </button>
                                              )
                                            })}
                                          </div>
                                        </div>
                                      )}

                                    {/* Instruções de Preenchimento (se disponível) */}
                                    {/* {proc.instrucoes && proc.instrucoes.trim() !== '' && (
                                      <div>
                                        <div className='flex items-center space-x-1.5 mb-2'>
                                          <HiOutlineLightBulb className='w-3.5 h-3.5 text-amber-600 dark:text-amber-400' />
                                          <h5 className='text-xs font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wide'>
                                            Instruções de Preenchimento
                                          </h5>
                                        </div>
                                        <div className='bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3'>
                                          <div 
                                            className='text-sm text-amber-900 dark:text-amber-100 prose prose-sm max-w-none'
                                            dangerouslySetInnerHTML={{ __html: proc.instrucoes }}
                                          />
                                        </div>
                                      </div>
                                    )} */}

                                    {/* Descrição Editável (campo vazio para o usuário preencher) */}
                                    <div>
                                      <div className='flex items-center space-x-1.5 mb-2'>
                                        <HiDocumentText className='w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400' />
                                        <label className='text-xs font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wide'>
                                          Descrição da Demanda *
                                        </label>
                                      </div>
                                      <Textarea
                                        variant='rich'
                                        value={customData.descricao}
                                        onChange={value => {
                                          setProcessCustomData(prevMap => {
                                            const newMap = new Map(prevMap)
                                            const currentData = newMap.get(proc.value) || {
                                              descricao: '',
                                              anexos: [],
                                            }
                                            newMap.set(proc.value, {
                                              ...currentData,
                                              descricao: value,
                                            })
                                            return newMap
                                          })
                                        }}
                                        placeholder='Edite ou complemente as instruções acima conforme necessário...'
                                        rows={4}
                                        className='border-gray-200 dark:border-neutral-700'
                                      />
                                    </div>

                                    {/* Anexos Específicos */}
                                    <div>
                                      <div className='flex items-center space-x-1.5 mb-2'>
                                        <HiPaperClip className='w-3.5 h-3.5 text-blue-600 dark:text-blue-400' />
                                        <label className='text-xs font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wide'>
                                          Anexos específicos para este processo
                                        </label>
                                      </div>
                                      <Input
                                        type='file'
                                        multiple
                                        accept='.pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar'
                                        onChange={e => {
                                          if (e.target.files) {
                                            const newFiles = Array.from(e.target.files)
                                            setProcessCustomData(prevMap => {
                                              const newMap = new Map(prevMap)
                                              const currentData = newMap.get(proc.value) || {
                                                descricao: '',
                                                anexos: [],
                                              }
                                              newMap.set(proc.value, {
                                                ...currentData,
                                                anexos: [...currentData.anexos, ...newFiles],
                                              })
                                              return newMap
                                            })
                                            e.target.value = ''
                                          }
                                        }}
                                        className='mb-2 cursor-pointer'
                                      />

                                      {/* Lista de anexos */}
                                      {customData.anexos.length > 0 && (
                                        <div className='space-y-1 mt-2'>
                                          {customData.anexos.map((file, i) => (
                                            <div
                                              key={i}
                                              className='flex items-center justify-between p-2 bg-emerald-50 dark:bg-emerald-900/20 rounded border border-emerald-200 dark:border-emerald-700'
                                            >
                                              <span className='text-sm text-emerald-700 dark:text-emerald-300 truncate flex-1'>
                                                {file.name}
                                              </span>
                                              <button
                                                type='button'
                                                onClick={() => {
                                                  setProcessCustomData(prevMap => {
                                                    const newMap = new Map(prevMap)
                                                    const currentData = newMap.get(proc.value) || {
                                                      descricao: '',
                                                      anexos: [],
                                                    }
                                                    newMap.set(proc.value, {
                                                      ...currentData,
                                                      anexos: currentData.anexos.filter(
                                                        (_, idx) => idx !== i
                                                      ),
                                                    })
                                                    return newMap
                                                  })
                                                }}
                                                className='ml-2 text-red-600 hover:text-red-800 flex-shrink-0'
                                              >
                                                <HiXMark className='w-4 h-4' />
                                              </button>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </StepContainer>
              )}

              {/* ETAPA 2: Detalhes da Demanda */}
              <StepContainer
                isActive={currentStep === 'demand-details'}
                isCompleted={completedSteps.has('demand-details')}
              >
                {/* Indicador de Solicitação Rápida */}
                {/* {fluxoSimplificadoAtivo && (
                  <div className='bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-4 mb-4 shadow-sm'>
                    <div className='flex items-center space-x-3'>
                      <div className='flex-shrink-0'>
                        <div className='w-10 h-10 bg-emerald-100 dark:bg-emerald-800/50 rounded-full flex items-center justify-center'>
                          <HiOutlineLightBulb className='w-6 h-6 text-emerald-600 dark:text-emerald-400' />
                        </div>
                      </div>
                      <div className='flex-1'>
                        <h4 className='text-sm font-semibold text-emerald-800 dark:text-emerald-200'>
                          Solicitação Rápida
                        </h4>
                        <p className='text-xs text-emerald-700 dark:text-emerald-300'>
                          Preencha apenas os campos abaixo. O título e demandante foram preenchidos automaticamente.
                        </p>
                      </div>
                    </div>
                  </div>
                )} */}

                {/* Header do Template ou Card de informação padronizado */}
                {selectedProcessObjects.length > 0 && (
                  <>
                    {/* Se o fluxo simplificado está ativo e deve exibir header do template */}
                    {fluxoSimplificadoAtivo?.config?.exibirHeaderTemplate ? (
                      <TemplateHeader
                        templateNome={
                          selectedProcessObjects[0]?.template_schema?.template_nome ||
                          'Formulário de Solicitação'
                        }
                        templateDescricao={
                          selectedProcessObjects[0]?.template_schema?.schema?.description
                        }
                        processoNome={fluxoSimplificadoAtivo?.processoNome}
                      />
                    ) : (
                      /* Card de informação padronizado (modo normal) */
                      !fluxoSimplificadoAtivo?.config?.ocultarInfoProcessos && (
                        <div className='bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-lg p-4 mb-3 shadow-sm'>
                          <div className='flex justify-between'>
                            <div className='space-y-2'>
                              <div className='flex items-center space-x-2 mb-3'>
                                <HiBuildingOffice2 className='w-4 h-4 text-primary dark:text-blue-400' />
                                <p className='text-sm text-primary dark:text-blue-300 font-semibold'>
                                  Será enviada para os seguintes processos:
                                </p>
                              </div>
                              {(() => {
                                // Agrupar processos por setor
                                const processesBySector = selectedProcessObjects.reduce(
                                  (acc, proc) => {
                                    const sector = setorOptions.find(
                                      s => s.value === proc.setor_processo_fk
                                    )
                                    const sectorName = sector?.label || 'Setor não encontrado'
                                    if (!acc[sectorName]) {
                                      acc[sectorName] = []
                                    }
                                    acc[sectorName].push(proc.label)
                                    return acc
                                  },
                                  {} as Record<string, string[]>
                                )

                                return Object.entries(processesBySector).map(
                                  ([sectorName, processNames]) => (
                                    <div
                                      key={sectorName}
                                      className='flex items-start space-x-2 text-sm'
                                    >
                                      <div className='w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0'></div>
                                      <div className='flex-1'>
                                        <span className='font-medium text-gray-900 dark:text-gray-100'>
                                          {sectorName}
                                        </span>
                                        <span className='text-gray-600 dark:text-gray-400 mx-2'>
                                          •
                                        </span>
                                        <span className='text-gray-700 dark:text-gray-300'>
                                          {processNames.join(', ')}
                                        </span>
                                      </div>
                                    </div>
                                  )
                                )
                              })()}
                            </div>
                            {customizationMode !== 'custom' &&
                              selectedProcessObjects.length > 0 &&
                              (() => {
                                const processesWithTimeline = selectedProcessObjects.reduce<
                                  Array<{
                                    key: string | number
                                    label: string
                                    sectorLabel: string
                                    timelineData: ReturnType<typeof buildProcessTimelinePreview>
                                  }>
                                >((acc, proc) => {
                                  const timelineData = buildProcessTimelinePreview(proc)

                            if (!timelineData.shouldRenderTimeline) {
                              return acc
                            }

                            const setorProcessoFk = (
                              proc as {
                                setor_processo_fk?: string | number
                              }
                            ).setor_processo_fk

                            const sector = setorOptions.find(s => s.value === setorProcessoFk)

                            acc.push({
                              key: (proc.value as string | number) ?? acc.length,
                              label: proc.label,
                              sectorLabel: sector?.label || 'Setor não encontrado',
                              timelineData,
                            })

                            return acc
                          }, [])

                          if (processesWithTimeline.length === 0) {
                            return null
                          }

                          return (
                            <div className='flex items-center ml-6'>
                              {processesWithTimeline.map(({ key, timelineData }) => (
                                <div key={key}>
                                  <AutorizacaoTimeline
                                    autorizacoes={timelineData.autorizacoesVisuais}
                                    isSequencial={timelineData.timelineIsSequencial}
                                    className='flex-1'
                                    previewMode={true}
                                    fixedRolePreview={timelineData.fixedRolePreview}
                                  />
                                </div>
                              ))}
                            </div>
                          )
                        })()}
                          </div>
                        </div>
                      )
                    )}
                  </>
                )}

                <div className='h-full flex flex-col justify-center'>
                  <div className='w-full max-w-7xl mx-auto'>
                    {/* Formulário compacto */}
                    <div className='space-y-4'>
                      {/* Card combinado: Título, Prioridade e Descrição */}
                      <div className='bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-xl p-5 shadow-sm hover:shadow-md transition-all duration-300 hover:border-primary/30'>
                        <div className='space-y-4'>
                          {/* Título e Prioridade na mesma linha - Ocultar se fluxo simplificado definir */}
                          {(!fluxoSimplificadoAtivo?.config?.ocultarTitulo ||
                            !fluxoSimplificadoAtivo?.config?.ocultarPrioridade) && (
                            <div className='grid grid-cols-1 lg:grid-cols-5 gap-4'>
                              {/* Título - 4 colunas (80%) */}
                              {!fluxoSimplificadoAtivo?.config?.ocultarTitulo && (
                                <div className='lg:col-span-4'>
                                  <div className='flex items-center space-x-2 mb-2'>
                                    <div className='w-2 h-2 bg-primary rounded-full'></div>
                                    <label className='text-sm font-semibold text-gray-800 dark:text-gray-200'>
                                      Título da demanda *
                                    </label>
                                  </div>
                                  <Input
                                    type='text'
                                    placeholder='Digite um título claro e objetivo...'
                                    value={titulo}
                                    onChange={e => handleTituloChange(e.target.value)}
                                    className='h-10 border-1 bg-background/50 shadow-none focus:bg-background/80 transition-all duration-300'
                                    disabled={tituloBlockado}
                                  />
                                </div>
                              )}

                              {/* Prioridade - 1 coluna (20%) */}
                              {!fluxoSimplificadoAtivo?.config?.ocultarPrioridade && (
                                <div className='lg:col-span-1'>
                                  <div className='flex items-center space-x-2 mb-2'>
                                    <div className='w-2 h-2 bg-primary rounded-full'></div>
                                    <label className='text-sm font-semibold text-gray-800 dark:text-gray-200'>
                                      Prioridade *
                                    </label>
                                  </div>
                                  <Select
                                    options={prioridadeOptions}
                                    value={prioridade}
                                    onChange={handlePrioridadeChange}
                                    placeholder='Prioridade'
                                    className='h-10 border-0 bg-background/50 shadow-none focus:bg-background/80 transition-all duration-300'
                                  />
                                </div>
                              )}
                            </div>
                          )}

                          {/* Descrição ou Template */}
                          <div>
                            {/* <div className='flex items-center space-x-2 mb-2'>
                              <div className='w-2 h-2 bg-primary rounded-full'></div>
                              <label className='text-sm font-semibold text-gray-800 dark:text-gray-200'>
                                {temProcessoComTemplate && selectedProcesses.length === 1
                                  ? 'Preencha o formulário *'
                                  : 'Descrição detalhada *'}
                              </label>
                            </div> */}
                            
                            {/* Se tiver um único processo com template, mostra o formulário dinâmico */}
                            {temProcessoComTemplate && selectedProcesses.length === 1 ? (
                              <div className='bg-gray-50 dark:bg-neutral-900 rounded-lg p-4 border border-gray-200 dark:border-neutral-700'>
                                <TemplateFormRenderer
                                  templateSchema={processosComTemplate[0]?.template_schema as TemplateSchema}
                                  formData={
                                    templateRespostas.get(selectedProcesses[0]) || 
                                    templateRespostas.get(Number(selectedProcesses[0])) || 
                                    templateRespostas.get(String(selectedProcesses[0])) || 
                                    {}
                                  }
                                  onChange={(data) => handleTemplateChange(selectedProcesses[0], data)}
                                />
                              </div>
                            ) : (
                              /* Sem template ou múltiplos processos, mostra descrição livre */
                              <Textarea
                                variant='rich'
                                value={descricao}
                                onChange={handleDescricaoChange}
                                placeholder='Descreva detalhadamente o que precisa ser feito...'
                                rows={3}
                                className='border-0 bg-background/50 shadow-none focus:bg-background/80 transition-all duration-300 [&_.editor]:border-0 [&_.editor]:shadow-none'
                              />
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Card compacto: Data e Anexos - Ocultar se fluxo simplificado definir */}
                      {!fluxoSimplificadoAtivo?.config?.ocultarDataEntrega &&
                        !fluxoSimplificadoAtivo?.config?.ocultarAnexos && (
                          <div className='bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-xl p-4 shadow-sm hover:shadow-md transition-all duration-300 hover:border-primary/30'>
                            <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
                              {/* Data de entrega */}
                              {!fluxoSimplificadoAtivo?.config?.ocultarDataEntrega && (
                                <div>
                                  <div className='flex items-center space-x-2 mb-2'>
                                    <div className='w-2 h-2 bg-primary rounded-full'></div>
                                    <label className='text-sm font-semibold text-gray-800 dark:text-gray-200'>
                                      Prazo de Entrega Sugerido*
                                    </label>
                                  </div>
                                  <Input
                                    type='date'
                                    value={dataEntrega}
                                    onChange={e => handleDataEntregaChange(e.target.value)}
                                    min={new Date().toISOString().split('T')[0]}
                                    className={cn(
                                      'h-10 border-0 bg-background/50 shadow-none focus:bg-background/80 transition-all duration-300',
                                      fieldErrors.dataEntrega
                                        ? 'border border-red-500 focus:border-red-500'
                                        : ''
                                    )}
                                  />
                                  {fieldErrors.dataEntrega && (
                                    <p className='text-xs text-red-600 mt-1 flex items-center space-x-1'>
                                      <span>⚠️</span>
                                      <span>{fieldErrors.dataEntrega}</span>
                                    </p>
                                  )}

                                  {/* Indicador de preenchimento automático */}
                                  {dataEntrega &&
                                    selectedProcesses.length > 0 &&
                                    !fieldErrors.dataEntrega && (
                                      <p className='text-xs text-gray-600 dark:text-gray-400 mt-1 flex items-center space-x-1'>
                                        <span>
                                          <HiOutlineLightBulb />
                                        </span>
                                        <span>
                                          Data preenchida automaticamente baseada no SLA. Você pode
                                          alterá-la se necessário.
                                        </span>
                                      </p>
                                    )}

                                  {loadingSLA &&
                                    selectedProcesses.length > 0 &&
                                    selectedProcessObjects.length === 0 && (
                                      <div className='mt-2 flex items-center space-x-2 text-xs text-gray-500'>
                                        <div className='w-3 h-3 border-2 border-gray-300 border-t-primary rounded-full animate-spin'></div>
                                        <span>Calculando SLA...</span>
                                      </div>
                                    )}
                                </div>
                              )}

                              {/* Anexos */}
                              {!fluxoSimplificadoAtivo?.config?.ocultarAnexos && (
                                <div>
                                  <div className='flex items-center space-x-2 mb-2'>
                                    <div className='w-2 h-2 bg-muted-foreground rounded-full'></div>
                                    <label className='text-sm font-semibold text-gray-800 dark:text-gray-200'>
                                      Anexos (opcional)
                                    </label>
                                  </div>
                                  <div className='relative border-2 border-dashed border-border/60 rounded-lg p-2 h-10 hover:border-primary/50 transition-all duration-300 bg-background/30 dark:bg-background/20 hover:bg-primary/5 dark:hover:bg-primary/10 group'>
                                    <Input
                                      type='file'
                                      multiple
                                      accept='.pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar'
                                      onChange={handleAddFiles}
                                      className='absolute inset-0 w-full h-full opacity-0 cursor-pointer'
                                    />
                                    <div className='flex items-center justify-center h-full text-center'>
                                      <HiPaperClip
                                        className={cn(
                                          'w-4 h-4 mr-2 transition-all duration-300',
                                          anexos.length > 0
                                            ? 'text-emerald-600 dark:text-emerald-400'
                                            : 'text-muted-foreground dark:text-gray-400 group-hover:text-primary dark:group-hover:text-primary'
                                        )}
                                      />
                                      <span
                                        className={cn(
                                          'text-xs font-medium transition-colors duration-300',
                                          anexos.length > 0
                                            ? 'text-emerald-600 dark:text-emerald-400'
                                            : 'text-muted-foreground dark:text-gray-400 group-hover:text-primary dark:group-hover:text-primary'
                                        )}
                                      >
                                        {anexos.length > 0 ? `Adicionar mais` : 'Selecionar'}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                      {/* Lista interativa de arquivos - Ocultar se anexos ocultos */}
                      {anexos.length > 0 && !fluxoSimplificadoAtivo?.config?.ocultarAnexos && (
                        <div className='bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-lg p-3 animate-in fade-in-0 slide-in-from-bottom-2 duration-500'>
                          <div className='flex items-center space-x-2 mb-2'>
                            <HiPaperClip className='w-3 h-3 text-emerald-600 dark:text-emerald-400' />
                            <span className='text-xs font-medium text-emerald-700 dark:text-emerald-400'>
                              {anexos.length} arquivo{anexos.length > 1 ? 's' : ''} anexado
                              {anexos.length > 1 ? 's' : ''}
                            </span>
                          </div>
                          <div className='space-y-1 max-h-16 overflow-y-auto'>
                            {anexos.map((file, index) => (
                              <div
                                key={index}
                                className='flex items-center justify-between p-2 hover:bg-emerald-100/50 dark:hover:bg-emerald-900/30 rounded group'
                              >
                                <div className='flex items-center space-x-2 min-w-0 flex-1'>
                                  <div className='w-1.5 h-1.5 bg-emerald-500 dark:bg-emerald-400 rounded-full flex-shrink-0'></div>
                                  <span className='text-xs text-emerald-700 dark:text-emerald-400 truncate'>
                                    {file.name}
                                  </span>
                                  <span className='text-xs text-emerald-600/70 dark:text-emerald-400/70 flex-shrink-0'>
                                    ({(file.size / 1024).toFixed(1)} KB)
                                  </span>
                                </div>
                                <button
                                  onClick={() => handleRemoveFile(index)}
                                  className='ml-2 p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-full transition-all duration-200 flex-shrink-0 opacity-60 hover:opacity-100'
                                  title='Remover arquivo'
                                >
                                  <HiXMark className='w-3 h-3 text-red-500' />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </StepContainer>

              {/* ETAPA 3: Identificação */}
              {!isFluxoSimplificado && (
                <StepContainer
                  isActive={currentStep === 'identification'}
                  isCompleted={completedSteps.has('identification')}
                >
                {/* Card de informação padronizado */}
                {selectedProcessObjects.length > 0 && (
                  <div className='bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-lg p-4 mb-6 shadow-sm'>
                    <div className='flex items-center space-x-2 mb-3'>
                      <HiBuildingOffice2 className='w-4 h-4 text-primary dark:text-blue-400' />
                      <p className='text-sm text-primary dark:text-blue-300 font-semibold'>
                        Será enviada para os seguintes processos:
                      </p>
                    </div>
                    <div className='space-y-2'>
                      {(() => {
                        // Agrupar processos por setor
                        const processesBySector = selectedProcessObjects.reduce(
                          (acc, proc) => {
                            const sector = setorOptions.find(
                              s => s.value === proc.setor_processo_fk
                            )
                            const sectorName = sector?.label || 'Setor não encontrado'
                            if (!acc[sectorName]) {
                              acc[sectorName] = []
                            }
                            acc[sectorName].push(proc.label)
                            return acc
                          },
                          {} as Record<string, string[]>
                        )

                        return Object.entries(processesBySector).map(
                          ([sectorName, processNames]) => (
                            <div key={sectorName} className='flex items-start space-x-2 text-sm'>
                              <div className='w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0'></div>
                              <div className='flex-1'>
                                <span className='font-medium text-gray-900 dark:text-gray-100'>
                                  {sectorName}
                                </span>
                                <span className='text-gray-600 dark:text-gray-400 mx-2'>•</span>
                                <span className='text-gray-700 dark:text-gray-300'>
                                  {processNames.join(', ')}
                                </span>
                              </div>
                            </div>
                          )
                        )
                      })()}
                    </div>
                  </div>
                )}
                <div className='h-full flex flex-col justify-center'>
                  <div className='w-full max-w-7xl mx-auto h-full'>
                    {/* Layout equilibrado em grid */}
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-6 mb-6'>
                      {/* Card do Demandante expandido */}
                      <div className='bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-300 hover:border-primary/30'>
                        <div className='flex items-center space-x-2 mb-4'>
                          <HiUser className='w-5 h-5 text-blue-600 dark:text-blue-400' />
                          <label className='text-base font-semibold text-gray-800 dark:text-gray-200'>
                            Demandante *
                          </label>
                        </div>

                        <div className='space-y-4'>
                          {/* Checkbox expandido */}
                          <div className='flex items-center w-full rounded-lg bg-background/50 dark:bg-background/30 border border-border/30 hover:bg-primary/5 dark:hover:bg-primary/10'>
                            <input
                              type='checkbox'
                              id='eu-criei'
                              checked={euCriei}
                              onChange={e => setEuCriei(e.target.checked)}
                              className='size-5 ml-3 space-x-3 p-4 text-primary border-border rounded focus:ring-primary/20 focus:ring-2'
                              />
                            <label
                              htmlFor='eu-criei'
                              className='text-base h-full space-x-3 p-4 text-gray-800 dark:text-gray-200 font-medium cursor-pointer flex-1'
                            >
                              Eu sou o demandante desta solicitação
                            </label>
                          </div>

                          {/* Select expandido */}
                          {!euCriei && (
                            <div className='animate-in fade-in-0 slide-in-from-top-2 duration-500'>
                              <Select
                                variant='user'
                                options={userOptions}
                                value={demandante}
                                onChange={handleDemandanteChange}
                                placeholder='Selecione quem está fazendo esta solicitação...'
                                searchable
                                className='border-0 bg-background/50 shadow-none focus:bg-background/80 transition-all duration-300 text-base'
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Card dos Observadores expandido */}
                      <div className='bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-300 hover:border-primary/30'>
                        <div className='flex items-center space-x-2 mb-4'>
                          <HiUsers className='w-5 h-5 text-purple-600 dark:text-purple-400' />
                          <label className='text-base font-semibold text-gray-800 dark:text-gray-200'>
                            Observadores (opcional)
                          </label>
                        </div>

                        <UserMultiSelect
                          variant='user-multi'
                          options={userOptions}
                          value={observadores}
                          onChange={handleObservadoresChange}
                          placeholder='Selecione pessoas que devem acompanhar esta demanda...'
                          maxDisplayItems={4}
                          className='border-0 bg-background/50 shadow-none focus:bg-background/80 transition-all duration-300 text-base'
                        />

                        <div className='mt-3 text-xs text-muted-foreground dark:text-gray-400 flex items-center space-x-2'>
                          <div className='w-1 h-1 bg-muted-foreground dark:bg-gray-400 rounded-full'></div>
                          <span>Essas pessoas receberão notificações sobre o progresso</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                </StepContainer>
              )}

              {/* ETAPA 4: Confirmação */}
              {!isFluxoSimplificado && (
                <StepContainer
                  isActive={currentStep === 'confirmation'}
                  isCompleted={completedSteps.has('confirmation')}
                >
                <div className='h-full flex flex-col'>
                  <div className='w-full max-w-7xl mx-auto'>
                    <div className='space-y-4'>
                      {/* Card 1: Informações Básicas */}
                      <div className='bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-xl p-5 shadow-sm'>
                        <div className='flex items-center space-x-2 mb-4'>
                          <HiDocumentText className='w-5 h-5 text-blue-600 dark:text-blue-400' />
                          <h3 className='text-lg font-bold text-gray-900 dark:text-gray-100'>
                            Informações Básicas
                          </h3>
                        </div>

                        <div className='space-y-3'>
                          {/* Título */}
                          <div>
                            <label className='text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide'>
                              Título
                            </label>
                            <p className='text-lg font-bold text-gray-900 dark:text-gray-100 mt-1'>
                              {titulo}
                            </p>
                          </div>

                          {/* Grid de Info Rápida */}
                          <div className='grid grid-cols-1 md:grid-cols-3 gap-3 pt-2'>
                            {/* Prioridade */}
                            <div className='bg-gray-50 dark:bg-neutral-900/50 rounded-lg p-3 border border-gray-200 dark:border-neutral-700'>
                              <div className='flex items-center space-x-2 mb-1'>
                                <HiExclamationCircle className='w-4 h-4 text-gray-600 dark:text-gray-400' />
                                <label className='text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase'>
                                  Prioridade
                                </label>
                              </div>
                              <div className='flex items-center space-x-2 mt-1'>
                                <div
                                  className={cn(
                                    'w-2.5 h-2.5 rounded-full',
                                    prioridade === 'Urgente'
                                      ? 'bg-red-500 animate-pulse'
                                      : prioridade === 'Alta'
                                        ? 'bg-orange-500'
                                        : prioridade === 'Media'
                                          ? 'bg-yellow-500'
                                          : 'bg-green-500'
                                  )}
                                ></div>
                                <span className='text-sm font-semibold text-gray-900 dark:text-gray-100'>
                                  {prioridadeOptions.find(p => p.value === prioridade)?.label ||
                                    prioridade}
                                </span>
                              </div>
                            </div>

                            {/* Data */}
                            <div className='bg-gray-50 dark:bg-neutral-900/50 rounded-lg p-3 border border-gray-200 dark:border-neutral-700'>
                              <div className='flex items-center space-x-2 mb-1'>
                                <HiCalendar className='w-4 h-4 text-gray-600 dark:text-gray-400' />
                                <label className='text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase'>
                                  Entrega
                                </label>
                              </div>
                              <p className='text-sm font-semibold text-gray-900 dark:text-gray-100 mt-1'>
                                {new Date(dataEntrega).toLocaleDateString('pt-BR', {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric',
                                })}
                              </p>
                            </div>

                            {/* Processos */}
                            <div className='bg-gray-50 dark:bg-neutral-900/50 rounded-lg p-3 border border-gray-200 dark:border-neutral-700'>
                              <div className='flex items-center space-x-2 mb-1'>
                                <HiClipboardDocumentList className='w-4 h-4 text-gray-600 dark:text-gray-400' />
                                <label className='text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase'>
                                  Processos
                                </label>
                              </div>
                              <p className='text-sm font-semibold text-gray-900 dark:text-gray-100 mt-1'>
                                {selectedProcessObjects.length}{' '}
                                {selectedProcessObjects.length === 1 ? 'processo' : 'processos'}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Card 2: Template preenchido (se o processo tiver template) */}
                      {temProcessoComTemplate && selectedProcesses.length === 1 && (() => {
                        const processoComTemplate = processosComTemplate[0]
                        const templateData = templateRespostas.get(selectedProcesses[0]) || 
                                            templateRespostas.get(Number(selectedProcesses[0])) || 
                                            templateRespostas.get(String(selectedProcesses[0])) || 
                                            {}
                        const hasData = Object.keys(templateData).length > 0
                        
                        if (!hasData) return null
                        
                        return (
                          <div className='bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-xl p-5 shadow-sm'>
                            <div className='flex items-center space-x-2 mb-3'>
                              <HiClipboardDocumentList className='w-5 h-5 text-blue-600 dark:text-blue-400' />
                              <h3 className='text-lg font-bold text-gray-900 dark:text-gray-100'>
                                {processoComTemplate?.template_schema?.template_nome || 'Formulário Preenchido'}
                              </h3>
                            </div>
                            <div className='bg-gray-50 dark:bg-neutral-900/50 rounded-lg p-4 border border-gray-200 dark:border-neutral-700'>
                              <TemplateFormRenderer
                                templateSchema={processoComTemplate?.template_schema as TemplateSchema}
                                formData={templateData}
                                onChange={() => {}} // Não permite alterações na revisão
                                readonly={true}
                                disabled={true}
                                className='pointer-events-none opacity-90'
                              />
                            </div>
                          </div>
                        )
                      })()}

                      {/* Card 2b: Descrição Geral (se houver e não for template) */}
                      {descricao && descricao.trim() && !(temProcessoComTemplate && selectedProcesses.length === 1) && (
                        <div className='bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-xl p-5 shadow-sm'>
                          <div className='flex items-center space-x-2 mb-3'>
                            <HiDocumentText className='w-5 h-5 text-blue-600 dark:text-blue-400' />
                            <h3 className='text-lg font-bold text-gray-900 dark:text-gray-100'>
                              Descrição Geral
                            </h3>
                          </div>
                          <div className='bg-gray-50 dark:bg-neutral-900/50 rounded-lg p-4 border border-gray-200 dark:border-neutral-700'>
                            <RichTextDisplay
                              content={descricao}
                              className='prose prose-sm max-w-none dark:prose-invert text-gray-700 dark:text-gray-300 leading-relaxed'
                            />
                          </div>
                        </div>
                      )}

                      {/* Card 3: Processos */}
                      <div className='bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-xl p-5 shadow-sm'>
                        <div className='flex items-center justify-between mb-4'>
                          <div className='flex items-center space-x-2'>
                            <HiClipboardDocumentList className='w-5 h-5 text-emerald-600 dark:text-emerald-400' />
                            <h3 className='text-lg font-bold text-gray-900 dark:text-gray-100'>
                              {selectedProcessObjects.length === 1
                                ? 'Processo Selecionado'
                                : 'Processos Selecionados'}
                            </h3>
                          </div>
                          <span className='px-3 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded-full text-xs font-semibold'>
                            {selectedProcessObjects.length}
                          </span>
                        </div>

                        <div className='space-y-2'>
                          {selectedProcessObjects.map((proc, index) => {
                            const sector = setorOptions.find(
                              s => s.value === proc.setor_processo_fk
                            )
                            const customData = processCustomData.get(proc.value)
                            const hasCustomData =
                              customizationMode === 'custom' &&
                              customData &&
                              (customData.descricao.trim() !== '' || customData.anexos.length > 0)
                            const isExpanded = expandedProcesses.has(proc.value)

                            const {
                              shouldRenderTimeline,
                              timelineIsSequencial,
                              autorizacoesVisuais,
                              fixedRolePreview,
                            } = buildProcessTimelinePreview(proc)

                            return (
                              <div
                                key={proc.value}
                                className='bg-gray-50 dark:bg-neutral-900/50 border border-gray-200 dark:border-neutral-700 rounded-lg overflow-hidden'
                              >
                                {/* Header do Processo - sempre visível */}
                                <div
                                  className={cn(
                                    'p-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-neutral-800 transition-colors',
                                    hasCustomData && 'select-none'
                                  )}
                                  onClick={() => hasCustomData && toggleProcessExpanded(proc.value)}
                                >
                                  <div className='flex items-center justify-between'>
                                    <div className='flex items-center space-x-3 flex-1 min-w-0'>
                                      <div className='w-7 h-7 bg-emerald-600 dark:bg-emerald-500 rounded-lg flex items-center justify-center text-white font-bold text-xs flex-shrink-0'>
                                        {index + 1}
                                      </div>
                                      <div className='flex-1 min-w-0'>
                                        <h4 className='font-semibold text-gray-900 dark:text-gray-100 text-sm leading-tight truncate'>
                                          {proc.label}
                                        </h4>
                                        <div className='flex items-center space-x-1.5 mt-0.5'>
                                          <HiBuildingOffice2 className='w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 flex-shrink-0' />
                                          <p className='text-xs text-gray-600 dark:text-gray-400 font-medium truncate'>
                                            {sector?.label}
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                    <div className='flex items-center space-x-2 flex-shrink-0 ml-2'>
                                      {/* Timeline de Autorizações */}
                                      {shouldRenderTimeline && (
                                        <div className='flex items-center gap-2'>
                                          <AutorizacaoTimeline
                                            autorizacoes={autorizacoesVisuais}
                                            isSequencial={timelineIsSequencial}
                                            className='flex-1'
                                            previewMode={true}
                                            fixedRolePreview={fixedRolePreview}
                                          />
                                        </div>
                                      )}
                                      {hasCustomData && (
                                        <>
                                          {isExpanded ? (
                                            <HiChevronUp className='w-4 h-4 text-gray-600 dark:text-gray-400' />
                                          ) : (
                                            <HiChevronDown className='w-4 h-4 text-gray-600 dark:text-gray-400' />
                                          )}
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                {/* Customizações Específicas - dobráveis */}
                                {hasCustomData && isExpanded && (
                                  <div className='px-3 pb-3 pt-2 border-t border-gray-200 dark:border-neutral-700 space-y-3 bg-white dark:bg-neutral-800/50'>
                                    {/* Descrição Customizada */}
                                    {customData.descricao.trim() !== '' && (
                                      <div>
                                        <div className='flex items-center space-x-1.5 mb-2'>
                                          <HiDocumentText className='w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400' />
                                          <label className='text-xs font-semibold text-emerald-700 dark:text-emerald-300 uppercase tracking-wide'>
                                            Descrição Específica
                                          </label>
                                        </div>
                                        <div className='bg-gray-50 dark:bg-neutral-900/70 rounded-lg p-3 border border-gray-200 dark:border-neutral-700'>
                                          <RichTextDisplay
                                            content={customData.descricao}
                                            className='prose prose-sm max-w-none dark:prose-invert text-gray-700 dark:text-gray-300 text-sm leading-relaxed'
                                          />
                                        </div>
                                      </div>
                                    )}

                                    {/* Anexos Específicos */}
                                    {customData.anexos.length > 0 && (
                                      <div>
                                        <div className='flex items-center space-x-1.5 mb-2'>
                                          <HiPaperClip className='w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400' />
                                          <label className='text-xs font-semibold text-emerald-700 dark:text-emerald-300 uppercase tracking-wide'>
                                            Anexos Específicos ({customData.anexos.length})
                                          </label>
                                        </div>
                                        <div className='space-y-1.5'>
                                          {customData.anexos.map((file, i) => (
                                            <div
                                              key={i}
                                              className='flex items-center space-x-2 p-2 bg-gray-50 dark:bg-neutral-900/70 rounded border border-gray-200 dark:border-neutral-700'
                                            >
                                              <HiPaperClip className='w-3.5 h-3.5 text-gray-600 dark:text-gray-400 flex-shrink-0' />
                                              <span className='text-xs text-gray-700 dark:text-gray-300 truncate flex-1 font-medium'>
                                                {file.name}
                                              </span>
                                              <span className='text-xs text-gray-500 dark:text-gray-400 flex-shrink-0'>
                                                {(file.size / 1024).toFixed(1)} KB
                                              </span>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            )
                          })}
                        </div>
                      </div>

                      {/* Cards de Pessoas e Anexos Gerais */}
                      <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
                        {/* Card 4: Pessoas */}
                        <div className='bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-xl p-5 shadow-sm'>
                          <div className='flex items-center space-x-2 mb-4'>
                            <HiUsers className='w-5 h-5 text-purple-600 dark:text-purple-400' />
                            <h3 className='text-lg font-bold text-gray-900 dark:text-gray-100'>
                              Pessoas
                            </h3>
                          </div>

                          <div className='space-y-3'>
                            {/* Demandante */}
                            <div className='bg-gray-50 dark:bg-neutral-900/50 rounded-lg p-3 border border-gray-200 dark:border-neutral-700'>
                              <label className='text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-2 block'>
                                Demandante
                              </label>
                              <div className='flex items-center space-x-3'>
                                <div className='w-9 h-9 bg-purple-600 dark:bg-purple-500 rounded-full flex items-center justify-center text-white flex-shrink-0 shadow-sm'>
                                  {euCriei ? (
                                    <HiUser className='w-5 h-5' />
                                  ) : (
                                    <span className='font-bold text-sm'>
                                      {userOptions
                                        .find(u => u.value === demandante)
                                        ?.label?.charAt(0)
                                        .toUpperCase() || '?'}
                                    </span>
                                  )}
                                </div>
                                <div>
                                  <p className='text-sm font-bold text-gray-900 dark:text-gray-100'>
                                    {euCriei
                                      ? 'Você'
                                      : userOptions.find(u => u.value === demandante)?.label ||
                                        'Não definido'}
                                  </p>
                                  <p className='text-xs text-gray-600 dark:text-gray-400'>
                                    Responsável
                                  </p>
                                </div>
                              </div>
                            </div>

                            {/* Observadores */}
                            {observadores.length > 0 && (
                              <div className='bg-gray-50 dark:bg-neutral-900/50 rounded-lg p-3 border border-gray-200 dark:border-neutral-700'>
                                <label className='text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-2 block'>
                                  Observadores ({observadores.length})
                                </label>
                                <div className='flex flex-wrap gap-1.5'>
                                  {observadores.map((obsId, index) => {
                                    const observer = userOptions.find(u => u.value === obsId)
                                    return (
                                      <div
                                        key={index}
                                        className='flex items-center space-x-1.5 bg-white dark:bg-neutral-800 px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-neutral-700'
                                      >
                                        <div className='w-5 h-5 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-semibold text-xs'>
                                          {observer?.label?.charAt(0).toUpperCase() || '?'}
                                        </div>
                                        <span className='text-xs font-medium text-gray-800 dark:text-gray-200'>
                                          {observer?.label || 'Usuário'}
                                        </span>
                                      </div>
                                    )
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Card 5: Anexos */}
                        {(() => {
                          // Calcular total de anexos (gerais + específicos por processo)
                          const anexosEspecificos = customizationMode === 'custom'
                            ? Array.from(processCustomData.entries()).flatMap(([procId, data]) => {
                                const processo = selectedProcessObjects.find(p => p.value === procId)
                                return data.anexos.map(file => ({
                                  file,
                                  processoNome: processo?.label || 'Processo'
                                }))
                              })
                            : []
                          const totalAnexos = anexos.length + anexosEspecificos.length
                          const hasAnexosGerais = anexos.length > 0
                          const hasAnexosEspecificos = anexosEspecificos.length > 0

                          return (
                            <div className='bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-xl p-5 shadow-sm'>
                              <div className='flex items-center justify-between mb-4'>
                                <div className='flex items-center space-x-2'>
                                  <HiPaperClip className='w-5 h-5 text-blue-600 dark:text-blue-400' />
                                  <h3 className='text-lg font-bold text-gray-900 dark:text-gray-100'>
                                    Anexos
                                  </h3>
                                </div>
                                <span className='px-2.5 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-xs font-semibold'>
                                  {totalAnexos}
                                </span>
                              </div>

                              {totalAnexos > 0 ? (
                                <div className='space-y-3 max-h-80 overflow-y-auto pr-1'>
                                  {/* Anexos Gerais */}
                                  {hasAnexosGerais && (
                                    <div>
                                      <p className='text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-2'>
                                        Anexos Gerais ({anexos.length})
                                      </p>
                                      <div className='space-y-1.5'>
                                        {anexos.map((file, index) => (
                                          <div
                                            key={`geral-${index}`}
                                            className='flex items-center space-x-2 p-2.5 bg-gray-50 dark:bg-neutral-900/50 rounded-lg border border-gray-200 dark:border-neutral-700'
                                          >
                                            <HiPaperClip className='w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0' />
                                            <div className='min-w-0 flex-1'>
                                              <p className='text-xs font-semibold text-gray-900 dark:text-gray-100 truncate'>
                                                {file.name}
                                              </p>
                                              <p className='text-xs text-gray-600 dark:text-gray-400'>
                                                {(file.size / 1024).toFixed(1)} KB
                                              </p>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Anexos Específicos por Processo */}
                                  {hasAnexosEspecificos && (
                                    <div>
                                      <p className='text-xs font-semibold uppercase tracking-wide mb-2'>
                                        Anexos por Processo ({anexosEspecificos.length})
                                      </p>
                                      <div className='space-y-1.5'>
                                        {anexosEspecificos.map((item, index) => (
                                          <div
                                            key={`especifico-${index}`}
                                            className='flex items-center space-x-2 p-2.5 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-700'
                                          >
                                            <HiPaperClip className='w-4 h-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0' />
                                            <div className='min-w-0 flex-1'>
                                              <p className='text-xs font-semibold text-gray-900 dark:text-gray-100 truncate'>
                                                {item.file.name}
                                              </p>
                                              <p className='text-xs text-emerald-600 dark:text-emerald-400'>
                                                {item.processoNome} • {(item.file.size / 1024).toFixed(1)} KB
                                              </p>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className='bg-gray-50 dark:bg-neutral-900/50 rounded-lg p-4 border border-gray-200 dark:border-neutral-700'>
                                  <div className='flex flex-col items-center justify-center py-3 text-center'>
                                    <HiPaperClip className='w-8 h-8 text-gray-400 dark:text-gray-500 mb-2' />
                                    <p className='text-sm text-gray-600 dark:text-gray-400 font-medium'>
                                      Nenhum anexo
                                    </p>
                                    <p className='text-xs text-gray-500 dark:text-gray-500 mt-1'>
                                      Sem arquivos anexados
                                    </p>
                                  </div>
                                </div>
                              )}
                            </div>
                          )
                        })()}
                      </div>
                    </div>

                    {/* Indicador de progresso durante upload */}
                    {(uploadStatus === 'validating' ||
                      uploadStatus === 'creating' ||
                      uploadStatus === 'uploading' ||
                      uploadStatus === 'success' ||
                      uploadStatus === 'error') && (
                      <UploadProgressIndicator
                        progress={uploadProgress}
                        status={uploadStatus}
                        message={uploadMessage}
                        isVisible={true}
                      />
                    )}

                    {/* Erro de submissão compacto */}
                    {submitError && uploadStatus !== 'success' && (
                      <div className='bg-destructive/10 border border-destructive/20 rounded-lg p-3 mb-4'>
                        <div className='flex items-center space-x-2'>
                          <div className='w-1.5 h-1.5 bg-destructive rounded-full'></div>
                          <p className='text-sm font-medium text-destructive'>
                            Erro: {submitError}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                </StepContainer>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
